// Centralised, frontend-only mock data.
// Backend layer can later replace each export with API calls without touching components.

import { Sprout, Utensils, BookOpen } from "lucide-react";
import { CAUSE_LABEL } from "@/lib/causes";

export const CATEGORIES = {
    trees: {
        key: "trees",
        label: CAUSE_LABEL.trees,
        short: CAUSE_LABEL.trees,
        icon: Sprout,
        idPrefix: "TREE",
        tintBg: "bg-seedh-surface-green",
        tintBorder: "border-seedh-green/30",
        accentText: "text-seedh-green",
        accentBg: "bg-seedh-green",
        soft: "#EAF5E8",
    },
    food: {
        key: "food",
        label: CAUSE_LABEL.food,
        short: CAUSE_LABEL.food,
        icon: Utensils,
        idPrefix: "FOOD",
        tintBg: "bg-seedh-surface-beige",
        tintBorder: "border-seedh-brown/30",
        accentText: "text-seedh-brown",
        accentBg: "bg-seedh-brown",
        soft: "#F3EEE8",
    },
    education: {
        key: "education",
        label: CAUSE_LABEL.education,
        short: CAUSE_LABEL.education,
        icon: BookOpen,
        idPrefix: "EDU",
        tintBg: "bg-seedh-surface-blue",
        tintBorder: "border-seedh-blue/30",
        accentText: "text-seedh-blue",
        accentBg: "bg-seedh-blue",
        soft: "#E6F1F7",
    },
};

export const CATEGORY_LIST = Object.values(CATEGORIES);

// ---- Live counters (homepage stats) ----
export const STATS = [
    { key: "trees", label: "Trees Planted", value: 1042, suffix: "" },
    { key: "food", label: "Food Packets Distributed", value: 8740, suffix: "" },
    { key: "children", label: "Children Supported", value: 612, suffix: "" },
    { key: "drives", label: "Active Drives", value: 24, suffix: "" },
];

// ---- Three Focus Areas content ----
export const FOCUS_AREAS = [
    {
        category: "trees",
        description:
            "Sponsor saplings and follow their growth — every tree carries a public ID, health log, and photo timeline.",
        example: "Each sapling is tagged with a unique public ID",
        tracking: "Tree ID · Location · Health updates",
        image: "/tre_plant.png",
        cta: "Sponsor a Tree",
    },
    {
        category: "food",
        description:
            "Fund food packet distributions and community relief drives for underprivileged children and families.",
        example: "Every drive is logged with beneficiary count & photos",
        tracking: "Drive ID · Beneficiary group · Distribution log",
        image: "/food_drive.png",
        imageClass: "object-contain scale-125",
        imageHoverClass: "group-hover:scale-[1.35]",
        cta: "Fund a Food Drive",
    },
    {
        category: "education",
        description:
            "Sponsor books, stationery, and learning material for children — with school-level distribution records.",
        example: "Kits are distributed with signed school-level receipts",
        tracking: "Support ID · School / area · Distribution photos",
        image: "/child_welf.png",
        cta: "Support Child Welfare",
    },
];

// ---- Recent updates (homepage live feed) ----
export const RECENT_UPDATES = [
    {
        id: "TREE-1042",
        category: "trees",
        title: "Neem sapling — maintenance check",
        location: "Sajjangarh, Udaipur",
        date: "Feb 22, 2026",
        status: "Alive",
        note: "Healthy growth observed; soil moisture good.",
        image:
            "https://images.unsplash.com/photo-1625758476104-f2ed6c81248f?auto=format&fit=crop&w=900&q=80",
    },
    {
        id: "FOOD-2026-07",
        category: "food",
        title: "320 food packets distributed",
        location: "Old city outreach",
        date: "Feb 19, 2026",
        status: "Completed",
        note: "Hot meals served to families near Hathi Pol.",
        image:
            "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=900&q=80",
    },
    {
        id: "EDU-2026-04",
        category: "education",
        title: "Stationery kits — 86 children",
        location: "Govt. Primary School, Bedla",
        date: "Feb 15, 2026",
        status: "Distributed",
        note: "Notebooks, pencils, geometry sets delivered.",
        image:
            "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=900&q=80",
    },
];

// ---- Tracker sample search results ----
export const TRACKING_SAMPLES = {
    trees: [
        {
            id: "TREE-1001",
            type: "Neem (Azadirachta indica)",
            location: "Sajjangarh slope, Udaipur",
            date: "Aug 12, 2025",
            status: "Alive",
            update: "Healthy canopy growth · last checked Feb 22, 2026",
            image:
                "https://images.unsplash.com/photo-1625758476104-f2ed6c81248f?auto=format&fit=crop&w=900&q=80",
        },
        {
            id: "TREE-1018-A",
            type: "Peepal (Ficus religiosa)",
            location: "Bedla hillside",
            date: "Sep 02, 2025",
            status: "Replaced",
            update: "Original sapling replaced after monsoon damage.",
            image:
                "https://images.unsplash.com/photo-1473773508845-188df298d2d1?auto=format&fit=crop&w=900&q=80",
        },
    ],
    food: [
        {
            id: "FOOD-2026-07",
            location: "Hathi Pol, Old City",
            date: "Feb 19, 2026",
            packets: 320,
            group: "Children & elderly",
            update: "Hot meals · cooked at foundation kitchen.",
            image:
                "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=900&q=80",
        },
        {
            id: "FOOD-2026-05",
            location: "Sukher community centre",
            date: "Jan 28, 2026",
            packets: 180,
            group: "Migrant families",
            update: "Dry ration packets — rice, dal, oil, spices.",
            image:
                "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=900&q=80",
        },
    ],
    education: [
        {
            id: "EDU-2026-04",
            location: "Govt. Primary School, Bedla",
            date: "Feb 15, 2026",
            children: 86,
            items: "Notebooks, pencils, geometry sets",
            update: "Distribution photographed; teachers signed receipts.",
            image:
                "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=900&q=80",
        },
        {
            id: "EDU-2026-02",
            location: "Anganwadi, Sajjangarh",
            date: "Jan 10, 2026",
            children: 42,
            items: "Storybooks, crayons, drawing books",
            update: "Reading hour conducted with volunteers.",
            image:
                "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=900&q=80",
        },
    ],
};

// ---- Gallery items ----
// Each item also carries a `photos` array used by the photo-grid modal.
const galleryPhotosFor = (cat) => {
    const pools = {
        trees: [
            "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1625758476104-f2ed6c81248f?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1473773508845-188df298d2d1?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1597673030062-0a0d1ee49b1c?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1574263867128-a3d5c1b1deae?auto=format&fit=crop&w=1200&q=80",
        ],
        food: [
            "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1518155317743-a8ff43ea6a5f?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1593113616828-6f22bca04e3a?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?auto=format&fit=crop&w=1200&q=80",
        ],
        education: [
            "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=1200&q=80",
        ],
        community: [
            "https://images.unsplash.com/photo-1559027615-cd4628902d4a?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=1200&q=80",
            "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80",
        ],
    };
    return pools[cat] || pools.community;
};

export const GALLERY_ITEMS = [
    {
        id: 1,
        category: "trees",
        title: "Sajjangarh corporate drive",
        date: "Aug 12, 2025",
        location: "Sajjangarh, Udaipur",
        image:
            "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80",
        span: "row-span-2",
        description:
            "A corporate group joined our community for a half-day plantation. 120 native species saplings were planted across the slope.",
        photos: galleryPhotosFor("trees"),
    },
    {
        id: 2,
        category: "food",
        title: "Community food relief — Hathi Pol",
        date: "Feb 19, 2026",
        location: "Old city, Udaipur",
        image:
            "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
        description:
            "320 hot meal packets cooked by our kitchen team, distributed to children and elderly residents.",
        photos: galleryPhotosFor("food"),
    },
    {
        id: 3,
        category: "education",
        title: "Stationery kits — Bedla school",
        date: "Feb 15, 2026",
        location: "Govt. Primary School, Bedla",
        image:
            "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80",
        description:
            "86 children received notebooks, pencils, and complete geometry sets ahead of the term.",
        photos: galleryPhotosFor("education"),
    },
    {
        id: 4,
        category: "community",
        title: "Volunteer onboarding",
        date: "Jan 22, 2026",
        location: "Foundation office",
        image:
            "https://images.unsplash.com/photo-1559027615-cd4628902d4a?auto=format&fit=crop&w=1200&q=80",
        span: "row-span-2",
        description:
            "Welcomed 18 new community volunteers across all three program areas.",
        photos: galleryPhotosFor("community"),
    },
    {
        id: 5,
        category: "trees",
        title: "Community plantation Sunday",
        date: "Jan 12, 2026",
        location: "Bedla hillside",
        image:
            "https://images.unsplash.com/photo-1473773508845-188df298d2d1?auto=format&fit=crop&w=1200&q=80",
        description:
            "Families spent the morning planting 78 saplings together along the Bedla hillside trail.",
        photos: galleryPhotosFor("trees"),
    },
    {
        id: 6,
        category: "education",
        title: "Storybook reading hour",
        date: "Jan 10, 2026",
        location: "Anganwadi, Sajjangarh",
        image:
            "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80",
        description:
            "Volunteers read with 42 children and donated crayons and drawing books.",
        photos: galleryPhotosFor("education"),
    },
    {
        id: 7,
        category: "food",
        title: "Dry ration drive — Sukher",
        date: "Jan 28, 2026",
        location: "Sukher community centre",
        image:
            "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=1200&q=80",
        description:
            "180 dry ration packets — rice, dal, oil and spices — delivered to migrant families.",
        photos: galleryPhotosFor("food"),
    },
    {
        id: 8,
        category: "community",
        title: "School awareness walk",
        date: "Dec 14, 2025",
        location: "Udaipur city schools",
        image:
            "https://images.unsplash.com/photo-1625758476104-f2ed6c81248f?auto=format&fit=crop&w=1200&q=80",
        description:
            "Awareness walk with 220 schoolchildren — talks on trees, hygiene, and learning.",
        photos: galleryPhotosFor("community"),
    },
];

// ---- Detail page records (Tree / Food / Child Welfare) ----
// Each maps ID → full record with photo timeline + key facts.
export const TREE_RECORDS = {
    "TREE-1001": {
        id: "TREE-1001",
        type: "trees",
        species: "Neem (Azadirachta indica)",
        location: "Sajjangarh slope, Udaipur",
        plantedOn: "Aug 12, 2025",
        status: "Alive",
        sponsor: "Anonymous donor",
        timeline: [
            {
                date: "Feb 22, 2026",
                title: "Maintenance check — healthy growth",
                note: "Soil moisture good. Mild pruning. New leaves observed.",
                image: "https://images.unsplash.com/photo-1625758476104-f2ed6c81248f?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Nov 14, 2025",
                title: "Post-monsoon check",
                note: "Sapling cleared the first monsoon strongly. Root mulch added.",
                image: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Aug 12, 2025",
                title: "Planted",
                note: "Sapling planted at Sajjangarh slope by Seedh field team and community volunteers.",
                image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80",
            },
        ],
    },
    "TREE-1018-A": {
        id: "TREE-1018-A",
        type: "trees",
        species: "Peepal (Ficus religiosa)",
        location: "Bedla hillside",
        plantedOn: "Sep 02, 2025",
        status: "Replaced",
        sponsor: "Anonymous donor",
        timeline: [
            {
                date: "Feb 10, 2026",
                title: "Replacement sapling — alive",
                note: "Replacement (suffix -A) is healthy and well-watered.",
                image: "https://images.unsplash.com/photo-1473773508845-188df298d2d1?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Dec 04, 2025",
                title: "Original sapling lost",
                note: "Original sapling did not survive heavy monsoon. Replacement assigned.",
                image: "https://images.unsplash.com/photo-1597673030062-0a0d1ee49b1c?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Sep 02, 2025",
                title: "Planted",
                note: "Sapling planted at Bedla hillside.",
                image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80",
            },
        ],
    },
};

export const FOOD_RECORDS = {
    "FOOD-2026-07": {
        id: "FOOD-2026-07",
        type: "food",
        location: "Hathi Pol, Old City",
        date: "Feb 19, 2026",
        packets: 320,
        group: "Children & elderly",
        sponsor: "Community contributions",
        timeline: [
            {
                date: "Feb 19, 2026 · evening",
                title: "Distribution complete",
                note: "320 hot meal packets distributed. Receipts photographed.",
                image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Feb 19, 2026 · afternoon",
                title: "Cooking & packing",
                note: "Foundation kitchen prepared dal-chawal-sabzi meals.",
                image: "https://images.unsplash.com/photo-1593113616828-6f22bca04e3a?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Feb 18, 2026",
                title: "Drive scheduled",
                note: "Beneficiary count finalised with local volunteers.",
                image: "https://images.unsplash.com/photo-1518155317743-a8ff43ea6a5f?auto=format&fit=crop&w=1200&q=80",
            },
        ],
    },
    "FOOD-2026-05": {
        id: "FOOD-2026-05",
        type: "food",
        location: "Sukher community centre",
        date: "Jan 28, 2026",
        packets: 180,
        group: "Migrant families",
        sponsor: "Community contributions",
        timeline: [
            {
                date: "Jan 28, 2026",
                title: "Dry rations delivered",
                note: "180 packets — rice, dal, oil, spices — handed over.",
                image: "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Jan 27, 2026",
                title: "Packing day",
                note: "Volunteers assembled ration packets in foundation office.",
                image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
            },
        ],
    },
};

export const EDUCATION_RECORDS = {
    "EDU-2026-04": {
        id: "EDU-2026-04",
        type: "education",
        location: "Govt. Primary School, Bedla",
        date: "Feb 15, 2026",
        children: 86,
        items: "Notebooks, pencils, geometry sets",
        sponsor: "Anonymous donor",
        timeline: [
            {
                date: "Feb 15, 2026",
                title: "Kits distributed",
                note: "86 children received their kits. Teachers signed receipts.",
                image: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Feb 12, 2026",
                title: "Kit assembly",
                note: "Volunteers packed kits at the foundation.",
                image: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Feb 06, 2026",
                title: "School coordination",
                note: "Class teachers shared the list of children per grade.",
                image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=1200&q=80",
            },
        ],
    },
    "EDU-2026-02": {
        id: "EDU-2026-02",
        type: "education",
        location: "Anganwadi, Sajjangarh",
        date: "Jan 10, 2026",
        children: 42,
        items: "Storybooks, crayons, drawing books",
        sponsor: "Anonymous donor",
        timeline: [
            {
                date: "Jan 10, 2026",
                title: "Reading hour conducted",
                note: "Volunteers read with 42 children for over an hour.",
                image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80",
            },
            {
                date: "Jan 08, 2026",
                title: "Books selected",
                note: "Storybooks chosen in Hindi and English for the age group.",
                image: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80",
            },
        ],
    },
};

// ---- Donate page presets ----
export const DONATE_PRESETS = {
    trees: [
        { label: "Sponsor 1 sapling", amount: 501 },
        { label: "Sponsor 5 saplings", amount: 2501 },
        { label: "Sponsor 10 saplings", amount: 5001 },
    ],
    food: [
        { label: "10 food packets", amount: 501 },
        { label: "50 food packets", amount: 2501 },
        { label: "Sponsor a drive", amount: 10001 },
    ],
    education: [
        { label: "1 child's stationery kit", amount: 751 },
        { label: "5 children's kits", amount: 3501 },
        { label: "Library set for a school", amount: 11001 },
    ],
};

// ---- Privacy / Terms placeholder content ----
export const POLICY_BLOCKS = {
    privacy: [
        {
            heading: "What we collect",
            body: "Names, email, phone, and donation transaction references submitted via our donation or contact forms. Public records (Tree / Drive / Support IDs) are intentionally public.",
        },
        {
            heading: "How we use it",
            body: "Verification of donations, mapping contributions to records, sending certificates, and responding to inquiries. We do not sell personal data.",
        },
        {
            heading: "Media & photographs",
            body: "Field photos may show drives, volunteers, and beneficiaries. We avoid capturing identifying child faces without guardian consent. Removal requests can be sent any time.",
        },
        {
            heading: "Cookies & analytics",
            body: "We use minimal analytics to understand site performance. No personalised advertising.",
        },
        {
            heading: "Contact",
            body: "Email seedhwelfaresociety@gmail.com for any data request, correction, or removal.",
        },
    ],
    terms: [
        {
            heading: "Public records",
            body: "Tree Plantation, Food Drive, and Child Welfare IDs are public and intended for transparent verification. By contributing, donors agree to having their contribution linked to a public ID (donor names remain private unless explicit permission is given).",
        },
        {
            heading: "Donations & certificates",
            body: "Donations are submitted via UPI and verified manually before being mapped to records. Certificates are issued on-demand and reflect publicly available record data.",
        },
        {
            heading: "Replacements & status changes",
            body: "Saplings may be replaced if they die in the first year — replacements use a suffixed ID. Food and education drives are immutable historical records once published.",
        },
        {
            heading: "Acceptable use",
            body: "Public lookup pages may not be scraped or reused for commercial purposes. Bulk access can be requested via email.",
        },
        {
            heading: "Liability",
            body: "Seedh Welfare Society makes best-effort guarantees on records but is not liable for indirect losses arising from misinterpretation of public data.",
        },
    ],
};
